<template>
    <v-row>
        <v-col cols="12" md="6" v-if="isCompany">
            <kimsa-combobox
                :placeholder="$t('inputs.coaches_placeholder')"
                outlined dense solo
                :items="coaches"
                @change="setCurrentCoach"
                :value="currentCoach"
                item-value="objectID"
                clearable searchable
                icon="$account"
            />
        </v-col>
        <v-col cols="12" :md="isCompany ? 6 : 12">
            <kimsa-combobox
                :placeholder="$t('inputs.students_placeholder')"
                outlined dense solo
                :items="students"
                @change="setCurrentStudents"
                :value="currentStudents"
                item-value="objectID"
                clearable searchable
                icon="$account"
                :readonly="isCompany && !currentCoach"
                multiple-with-chips
            />
        </v-col>
        <v-col cols="12" class="text-center">
            <div class="subtitle-text">
                {{ $t('routines.select_days_to_assign') }}
            </div>
            <mini-calendar :value="dates" @change="dates = $event"/>
        </v-col>

        <select-replace-assigned-routines-dates-dialog
            :assigned-routines-in-same-date="assignedRoutinesInSameDate"
            no-close-dialog
            @confirm="onConfirmReplace($event)"
            ref="replaceRoutinesDates"
        />
    </v-row>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import {Client, UserIndex} from "@/utils/Algolia";
import MiniCalendar from "@/components/common/MiniCalendar";
import {db, FieldValue, Timestamp} from "@/utils/Firebase";
import SelectReplaceAssignedRoutinesDatesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDatesDialog";
import {
    clearBlocksAssignedRoutine,
    deleteAssignedRoutinesGroup,
    getPayloadBaseAssignedRoutine,
    hasRoutinesInDates
} from "@/utils/Exercises";

export default {
    name: "AssignRoutineSelector",
    components: {SelectReplaceAssignedRoutinesDatesDialog, MiniCalendar, KimsaCombobox},
    props: {
        routine: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            coaches: [],
            students: [],
            currentCoach: null,
            currentStudents: [],
            dates: [],
            loading: false,
            assignedRoutinesInSameDate: [],
            datesToSkip: [],
        }
    },
    computed: {
        isCompany(vm = this) {
            return vm.account?.accountType === 'company'
        },
        coachId(vm = this) {
            return vm.isCompany ?
                vm.currentCoach?.objectID || null :
                vm.currentUser.uid
        },
        isValid(vm = this) {
            return vm.dates.length > 0 && vm.currentStudents.length > 0
        },
        routineId(vm = this) {
            return vm.$route.params.uid || null
        }
    },
    async mounted() {
        const vm = this
        vm.isCompany ? await vm.loadCoaches() : await vm.loadStudents()
    },
    methods: {
        async loadCoaches() {
            const vm = this
            let filter = `coachSubscription.companyId: ${vm.currentUser.uid} AND coachSubscription.paused != 1`
            await Client.clearCache()
            let response = await UserIndex.search('', {filters: filter, hitsPerPage: 10000})
            vm.coaches = response.hits.map(hit => ({...hit, name: hit.name || hit.email}))
            let coachId = vm.$route.query.coachId
            if (coachId) vm.currentCoach = vm.coaches.find(coach => coach.objectID === coachId)
        },
        async loadStudents() {
            const vm = this
            let filter = `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`
            await Client.clearCache()
            let response = await UserIndex.search('', {filters: filter, hitsPerPage: 10000})
            vm.students = response.hits
            let studentId = vm.$route.query.studentId
            if (studentId) {
                let student = vm.students.find(student => student.objectID === studentId)
                if (student) vm.currentStudents.push(student)
            }
        },
        setCurrentCoach(coach) {
            const vm = this
            vm.currentCoach = coach?.name ? coach : vm.coaches?.find(co => co.name.toLowerCase() === coach?.toLowerCase()) || null
        },
        setCurrentStudents(students) {
            const vm = this
            if (students?.length === 0) return vm.currentStudents = students
            let currentStudents = [...students]
            let lastStudent = currentStudents[currentStudents.length - 1] || null
            currentStudents = currentStudents.filter(st => typeof st !== 'string')

            if (typeof lastStudent === 'string') {
                let foundStudent = vm.students?.find(student => student.name.toLowerCase() === lastStudent?.toLowerCase()) || null
                foundStudent ? vm.currentStudents.push(foundStudent) : vm.currentStudents = vm.currentStudents.filter(st => typeof st !== 'string')
            } else vm.currentStudents = students
        },
        async checkAssignedRoutines() {
            const vm = this
            vm.loading = true
            await vm.loadRoutinesInDate()
            if (vm.assignedRoutinesInSameDate.length === 0) return await vm.assign()
            vm.loading = false
            return vm.$refs.replaceRoutinesDates.show()
        },
        async loadRoutinesInDate() {
            const vm = this
            vm.assignedRoutinesInSameDate = []

            for (let student of vm.currentStudents) {
                let routines = await hasRoutinesInDates(vm.dates, student.objectID)
                vm.assignedRoutinesInSameDate.push(...routines.map(routine => ({
                    assignedRoutineId: routine.id,
                    date: vm.$moment(routine.rawDate.full).format('DD-MM-YYYY'),
                    name: student.name,
                    replace: false,
                })))
            }
        },
        async onConfirmReplace(toReplace) {
            const vm = this
            vm.loading = true

            let deleteIds = toReplace.filter(routine => routine.replace).map(routine => routine.assignedRoutineId)
            await deleteAssignedRoutinesGroup(deleteIds)

            vm.datesToSkip = toReplace.filter(routine => !routine.replace).map(routine => ({
                date: vm.$moment(routine.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                name: routine.name
            }))
            await vm.assign()
        },
        async assign() {
            const vm = this
            let counter = 0
            let dateNameSet = new Set(vm.datesToSkip.map(item => JSON.stringify(item)))
            let routine = {...vm.routine, coachId: vm.coachId, routineId: vm.routineId}
            let blocks = clearBlocksAssignedRoutine(routine.blocks)

            for (let student of vm.currentStudents) {
                counter++
                for (let date of vm.dates) {
                    counter++
                    const formattedDate = vm.$moment(date).format('DD-MM-YYYY')
                    if (!dateNameSet.has(JSON.stringify({date: formattedDate, name: student.name}))) {
                        const routineName = `${student.name} - ${formattedDate}`
                        let payload = getPayloadBaseAssignedRoutine(routineName, date, routine, blocks)
                        payload.studentId = student.objectID
                        await db.collection('assignedRoutines').add(payload)
                    }
                }
            }
            await db.collection('routines').doc(vm.routineId).update({used: FieldValue.increment(counter)})

            vm.toast(vm.$t('routines.assigned_routines_successfully'))
            vm.$emit('success')
            vm.loading = false
        },
    },
    watch: {
        async currentCoach(val) {
            const vm = this
            vm.currentStudents = []
            await vm.loadStudents()
        },
        loading(val) {
            this.$emit('loading', val)
        },
        isValid(val) {
            this.$emit('valid', val)
        }
    }
}
</script>

<style scoped>

.subtitle-text {
    font-weight: 300;
    font-size: 14px;
    line-height: 12px;
    color: #03342C;
}

</style>